import React, { useRef, useState } from "react";
import { X, UploadCloud, Link2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const inputCls =
  "w-full bg-obsidian-900 border border-obsidian-600 px-2.5 py-2 rounded-sm focus:border-blaze focus:outline-none focus:ring-1 focus:ring-blaze text-sm";
const labelCls = "block text-[10px] uppercase tracking-widest text-zinc-500 mb-1";

const CATEGORIES = [
  ["technique", "Technique"],
  ["warmup", "Warm-up"],
  ["mobility", "Mobility"],
  ["program", "Program"],
  ["nutrition", "Nutrition"],
  ["other", "Other"],
];

const CHUNK_BYTES = 4 * 1024 * 1024; // 4MB chunks slip under proxy limits

// Coach modal: add a video by uploading a file (chunked) or pasting a link.
export const AddVideoModal = ({ onClose, onCreated }) => {
  const [tab, setTab] = useState("upload");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("technique");
  const [exerciseName, setExerciseName] = useState("");
  const [url, setUrl] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileRef = useRef(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return toast.error("Give the video a title");
    setBusy(true);
    try {
      if (tab === "link") {
        if (!/^https?:\/\//i.test(url.trim())) {
          setBusy(false);
          return toast.error("Paste a full link starting with http(s)://");
        }
        const r = await api.post("/videos", { title, description, category, url: url.trim(), exercise_name: exerciseName });
        toast.success("Video added to your library");
        onCreated(r.data);
        return;
      }
      // Chunked file upload
      if (!file) {
        setBusy(false);
        return toast.error("Choose a video file first");
      }
      if (file.size > 200 * 1024 * 1024) {
        setBusy(false);
        return toast.error("Max size is 200MB");
      }
      const { data: start } = await api.post("/videos/upload/start");
      const uploadId = start.upload_id;
      let sent = 0;
      while (sent < file.size) {
        const blob = file.slice(sent, sent + CHUNK_BYTES);
        const form = new FormData();
        form.append("upload_id", uploadId);
        form.append("chunk", blob, "chunk.bin");
        await api.post("/videos/upload/chunk", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        sent += blob.size;
        setProgress(Math.round((sent / file.size) * 100));
      }
      const r = await api.post("/videos/upload/complete", {
        upload_id: uploadId,
        filename: file.name,
        title,
        description,
        category,
        exercise_name: exerciseName,
      });
      toast.success("Video uploaded to your library");
      onCreated(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add video");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-40 grid place-items-center p-4" data-testid="add-video-modal">
      <div className="bg-obsidian-800 border border-obsidian-600 rounded-sm w-full max-w-lg max-h-[90vh] overflow-y-auto p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-2xl uppercase">Add video</h3>
          <button type="button" onClick={onClose} data-testid="add-video-close" className="p-1 text-zinc-500 hover:text-blaze" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex gap-2 mb-4">
          <button
            type="button"
            data-testid="add-video-tab-upload"
            onClick={() => setTab("upload")}
            className={`px-4 py-2 text-xs uppercase tracking-widest rounded-sm border ${tab === "upload" ? "border-blaze text-blaze bg-blaze/10" : "border-obsidian-600 text-zinc-400"}`}
          >
            <UploadCloud className="h-3 w-3 inline mr-1.5" /> Upload file
          </button>
          <button
            type="button"
            data-testid="add-video-tab-link"
            onClick={() => setTab("link")}
            className={`px-4 py-2 text-xs uppercase tracking-widest rounded-sm border ${tab === "link" ? "border-blaze text-blaze bg-blaze/10" : "border-obsidian-600 text-zinc-400"}`}
          >
            <Link2 className="h-3 w-3 inline mr-1.5" /> Paste link
          </button>
        </div>

        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className={labelCls}>Title</label>
            <input data-testid="add-video-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. RDL setup cues" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Description (optional)</label>
            <textarea data-testid="add-video-description" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} placeholder="What should clients look for?" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Category</label>
            <select data-testid="add-video-category" value={category} onChange={(e) => setCategory(e.target.value)} className={inputCls}>
              {CATEGORIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Linked exercise (optional)</label>
            <input
              data-testid="add-video-exercise"
              value={exerciseName}
              onChange={(e) => setExerciseName(e.target.value)}
              placeholder="e.g. Romanian Deadlift — shows a cue-video button in workouts"
              className={inputCls}
            />
          </div>

          {tab === "link" ? (
            <div>
              <label className={labelCls}>Video link (YouTube, Vimeo or direct .mp4)</label>
              <input data-testid="add-video-url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://youtube.com/watch?v=..." className={inputCls} />
            </div>
          ) : (
            <div>
              <label className={labelCls}>Video file (mp4 / mov / webm, max 200MB)</label>
              <input
                ref={fileRef}
                data-testid="add-video-file"
                type="file"
                accept="video/mp4,video/quicktime,video/webm,video/x-m4v"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="block w-full text-sm text-zinc-400 file:mr-3 file:blaze-btn file:border-0 file:px-4 file:py-2 file:text-xs file:cursor-pointer"
              />
              {busy && progress > 0 && (
                <div className="mt-2">
                  <div className="h-1.5 bg-obsidian-600 rounded-full overflow-hidden">
                    <div className="h-full bg-blaze transition-all" style={{ width: `${progress}%` }} />
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1" data-testid="upload-progress">{progress}% uploaded</div>
                </div>
              )}
            </div>
          )}

          <button
            type="submit"
            data-testid="add-video-submit"
            disabled={busy}
            className="blaze-btn w-full py-2.5 text-xs disabled:opacity-50"
          >
            {busy ? (tab === "upload" ? `Uploading… ${progress}%` : "Saving…") : "Add to library"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AddVideoModal;
